import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import * as MailboxActions from '../../store/actions/mailbox.actions';
import { Mailbox } from '../../models/mailboxes.model';
import { Store } from '@ngrx/store';
import {
  ApplicationState,
  getAliases,
  getMailboxes,
  getMailboxesErrorInfo,
  getSelectedMailbox,
  getShowCreateMailboxLink,
} from '../../../store/reducers';
import { ActivatedRoute } from '@angular/router';
import { EnvironmentService, TranslationService } from '@proximus/ng-common';
import {
  LevelType,
  MessageType,
  ModalService,
  PxModalMessageService,
} from '@proximus/ng-common-components';
import { ErrorCode } from '../../models/errors.model';
import { CreateMailboxContainerComponent } from '../create-mailbox/create-mailbox-container.component';
import { combineLatest, filter, map, withLatestFrom } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { BrandService } from 'src/app/services/brand.service';

@Component({
  selector: 'mprd-mailboxes-container',
  templateUrl: 'mailboxes-container.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MailboxesContainerComponent implements OnInit {
  showCreateMailboxLink$: Observable<boolean>;
  mailboxes$: Observable<Mailbox[]>;
  selectedMailbox$: Observable<Mailbox>;
  mobileView: boolean;

  constructor(
    private store: Store<ApplicationState>,
    private activatedRoute: ActivatedRoute,
    private environmentService: EnvironmentService,
    private pxModalMessageService: PxModalMessageService,
    private translationService: TranslationService,
    private modalService: ModalService,
    private readonly brandService: BrandService,
  ) {}

  ngOnInit() {
    this.mobileView = this.environmentService.isMobile;
    this.mailboxes$ = this.store.select(getAliases).pipe(
      combineLatest(this.store.select(getMailboxes)),
      map(([aliasEntries, mailboxes]) =>
        mailboxes.map((mailbox: Mailbox) => {
          const mailboxAliases = aliasEntries[mailbox.mbxId];
          const displayName =
            mailboxAliases !== undefined && mailboxAliases.length > 0
              ? mailboxAliases[0].aliasId
              : mailbox.mbxId;
          return {
            ...mailbox,
            displayName,
          };
        }),
      ),
    );
    this.changeTheMailBoxSelectionIfMailIdParameterPresent();
    this.selectedMailbox$ = this.store.select(getSelectedMailbox).pipe(
      filter((v) => v !== undefined),
      withLatestFrom(
        this.store.select(getAliases),
        (mailbox, aliasEntries) => ({
          ...mailbox,
          displayName:
            aliasEntries[mailbox.mbxId] &&
            aliasEntries[mailbox.mbxId].length > 0
              ? aliasEntries[mailbox.mbxId][0].aliasId
              : mailbox.mbxId,
        }),
      ),
    );

    if (!this.brandService.isScarlet()) {
      this.showCreateMailboxLink$ = this.store.select(getShowCreateMailboxLink);
    }

    this.subscribeToMailboxErrors();
  }

  private subscribeToMailboxErrors() {
    this.store
      .select(getMailboxesErrorInfo)
      .pipe(filter(Boolean))
      .subscribe((error: any) => {
        if (error.code === ErrorCode.MailboxLoadFail) {
          this.pxModalMessageService.dispatchMessage(
            MessageType.Error,
            LevelType.Root,
            this.translationService.getTranslatedValueFor(
              'mailbox.technical.error.message',
            ),
            this.translationService.getTranslatedValueFor(
              'mailbox.technical.error.message.title',
            ),
          );
        }
        if (error.code === ErrorCode.MailBoxesNotFound) {
          this.pxModalMessageService.dispatchMessage(
            MessageType.Info,
            LevelType.Root,
            this.translationService.getTranslatedValueFor(
              'mymail.no.mailboxes.message',
            ),
            this.translationService.getTranslatedValueFor(
              'mymail.no.mailboxes.message.title',
            ),
          );
        }
      });
  }

  private changeTheMailBoxSelectionIfMailIdParameterPresent() {
    const selectedMailId = this.activatedRoute.snapshot.queryParams['mailId'];
    if (selectedMailId) {
      this.mailboxes$.subscribe((mailboxes: Mailbox[]) => {
        if (mailboxes && mailboxes.length > 0) {
          const foundMailbox = mailboxes.filter(
            (mailbox: Mailbox) => mailbox.mbxId === selectedMailId,
          )[0];
          if (foundMailbox) {
            this.store.dispatch(new MailboxActions.SelectAction(foundMailbox));
            this.store.dispatch(
              new MailboxActions.OpenMailboxAction(foundMailbox),
            );
          }
        }
      });
    }
  }

  onSelectMailbox(mailbox: Mailbox) {
    this.store.dispatch(new MailboxActions.SelectAction(mailbox));
    this.store.dispatch(new MailboxActions.OpenMailboxAction(mailbox));
  }

  trackByFn(index: number, item: Mailbox) {
    return item.mbxId;
  }

  createMailBox() {
    this.modalService
      .create<CreateMailboxContainerComponent>(CreateMailboxContainerComponent)
      .subscribe((componentRef) => {});
  }
}
